import { createSelector } from '@reduxjs/toolkit'
import { useEffect } from 'react'
import type { EnhancedManCodeId } from '#api/enhancements/enhancedTmsApiTypes'
import { useAppNavigation } from '#app/navigation/NavigationContext'
import { appRoutes } from '#app/navigation/routes/routes'
import { type RootState, useAppSelector } from '#app/store'
import type { LocalConfigurationId, TruckConfiguration } from '#app/store/slices/configurationSlice'
import { searchParamsFromApiConfig } from '#utils/parseApiConfigurationFromSearch'
import { codeMatcher, type GroupResult } from './ConfigurationContext'
import { useConfigurationContext } from './useConfigurationContext'

export const findConfigurationByUrlParam = createSelector(
  [(state: RootState) => state.truckConfigurations.configurations, (_state: RootState, parameter?: string) => parameter],
  (configurations, parameter): TruckConfiguration | undefined => {
    const { manCode, localId, sortOrder } = (parameter?.match(codeMatcher)?.groups as GroupResult) ?? {}
    const configList = Object.values(configurations).filter((x) => !!x)
    if (manCode) {
      const sortOrderNumber = Number.parseInt(sortOrder ?? '', 10)
      return configList.find((x) => x.apiConfiguration.manCode === manCode && x.sortOrder === sortOrderNumber)
    }
    if (localId) {
      return configList.find((x) => x.id === localId)
    }
    return undefined
  },
)

export const configurationSelector = createSelector(
  [
    (state: RootState) => state.truckConfigurations.configurations,
    (_state: RootState, configId?: LocalConfigurationId | EnhancedManCodeId) => configId,
    (_: RootState, _configId, sortOrder?: string) => sortOrder,
  ],
  (configurations, configId, sortOrder): [TruckConfiguration | undefined, TruckConfiguration[]] => {
    const configList = Object.values(configurations).filter((x) => !!x)
    if (configId && isLocalConfigurationId(configId)) {
      return [configurations[configId], configList]
    }
    // has to be a TMS config ID then
    if (configId) {
      const sortOrderNumber = Number.parseInt(sortOrder ?? '', 10)
      return [configList.find((x) => x.apiConfiguration.manCode === configId && x.sortOrder === sortOrderNumber), configList]
    }
    return [undefined, configList]
  },
)

export const isLocalConfigurationId = (id: string | undefined): id is LocalConfigurationId =>
  !!(id?.startsWith('MAIC-') && !!(id.match(codeMatcher)?.groups as GroupResult)?.localId)

export const isTmsConfigurationId = (id: string | undefined): id is `${EnhancedManCodeId}:${number}` =>
  !!(id?.startsWith('TMS-') && !!(id.match(codeMatcher)?.groups as GroupResult)?.manCode)

export const useActiveConfiguration = (): TruckConfiguration | undefined => {
  const { configurationId } = useConfigurationContext()
  const { manCode, localId, sortOrder } = (configurationId?.match(codeMatcher)?.groups as GroupResult) ?? {}
  const { navigate } = useAppNavigation()
  const [configuration, configurations] = useAppSelector((state) => configurationSelector(state, manCode ?? localId, sortOrder))
  useEffect(() => {
    // redirect to the setup page if no configuration is found
    if (!configuration && configurationId && isLocalConfigurationId(configurationId) && !configurations.find((x) => x.id === configurationId)) {
      navigate(appRoutes.setup.make())
    }

    // load tms configuration via MAN-Code Service if it is not already loaded
    else if (!configuration && isTmsConfigurationId(configurationId)) {
      const { manCode } = (configurationId.match(codeMatcher)?.groups as GroupResult) ?? {}
      if (!manCode) {
        return
      }

      const element = configurations.findIndex((x) => 'manCode' in x.callingApiConfiguration && x.callingApiConfiguration.manCode === manCode)
      // prevent creation of two configurations if there's already one with the same TMS parameters
      if (element) {
        return
      }
      // this is the last fallback: no MAN code has been created and it's nowhere to be found - the source cannot be claimed anymore.
      navigate(appRoutes['configuration/new'].make({}), {
        path: {
          search: searchParamsFromApiConfig({ apiConfig: { manCode: manCode as EnhancedManCodeId, mode: 'writable', source: undefined } }).toString(),
        },
      })
    }
  }, [configuration, configurationId, configurations, navigate])

  return configuration
}
